import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight, X, Image as ImageIcon } from 'lucide-react';
import ImageGallery from '../../components/ImageGallery';
import StorageVideoUpload from '../../components/admin/StorageVideoUpload';
import { buildProgramPathSegments } from '../../utils/programStoragePaths';
import '../../styles/admin/Programs.css';

const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000');

const emptyForm = { 
  program_id: '',
  name: '', 
  tagline: '', 
  description: '',
  duration: '',
  time_commitment: '',
  price: '', 
  price_inr: '',
  image: '', 
  download_image: '',
  intro_video_url: '',
  intro_storage_file_uuid: '',
  intro_video_height: 150,
  intro_video_width: 180,
  program_type: 'type1',
  whatsapp_link: '',
  color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  badge: '',
  features: '[]',
  program_offers: [],
  is_active: 1
};

export default function Programs() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [imageSelectionType, setImageSelectionType] = useState('main'); // 'main' or 'download'
  const [layoutConfirmOpen, setLayoutConfirmOpen] = useState(false);
  const [pendingLayoutType, setPendingLayoutType] = useState(null);
  const [layoutConfirmText, setLayoutConfirmText] = useState('');

  const fetchPrograms = async () => {
    try {
      // Use admin endpoint to get all programs including inactive
      const res = await fetch(`${API_URL}/api/programs/admin/all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        console.log('Fetched programs:', data.map(p => ({ id: p.program_id, image: p.image })));
        setPrograms(data);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (program) => {
    console.log('Opening program for edit:', program.program_id, 'Price:', program.price, 'Price INR:', program.price_inr);
    
    // Fix image path - handle both legacy paths and full URLs
    let fixedImage = program.image || '';
    let fixedDownloadImage = program.download_image || '';
    
    // Remove API_URL if present (backend prepends it)
    if (fixedImage.startsWith(API_URL)) {
      fixedImage = fixedImage.substring(API_URL.length);
    }
    if (fixedDownloadImage.startsWith(API_URL)) {
      fixedDownloadImage = fixedDownloadImage.substring(API_URL.length);
    }
    
    // Fix legacy paths
    if (fixedImage.startsWith('/programs/')) {
      fixedImage = `/uploads${fixedImage}`;
    }
    if (fixedDownloadImage.startsWith('/programs/')) {
      fixedDownloadImage = `/uploads${fixedDownloadImage}`;
    }
    
    console.log('Original image:', program.image);
    console.log('Fixed image:', fixedImage);
    console.log('Download image:', fixedDownloadImage);
    console.log('Full URL:', getImageUrl(fixedImage));

    let offers = [];
    try {
      if (Array.isArray(program.program_offers)) {
        offers = program.program_offers;
      } else if (program.program_offers) {
        offers = JSON.parse(program.program_offers);
      }
    } catch (e) {
      console.warn('Failed to parse program offers for edit', e);
      offers = [];
    }
    
    setEditingId(program.program_id);
    setForm({
      program_id: program.program_id,
      name: program.name,
      tagline: program.tagline || '',
      description: program.description || '',
      duration: program.duration || '',
      time_commitment: program.time_commitment || '',
      price: program.price || '',
      price_inr: program.price_inr || '',
      image: fixedImage,
      download_image: fixedDownloadImage,
      intro_video_url: program.intro_video_url || '',
      intro_storage_file_uuid: program.intro_storage_file_uuid || '',
      intro_video_height: program.intro_video_height || 150,
      intro_video_width: program.intro_video_width || 180,
      program_type: program.program_type || 'type1',
      whatsapp_link: program.whatsapp_link || '',
      color: program.color || '',
      badge: program.badge || '',
      features: JSON.stringify(program.features || []),
      program_offers: offers,
      is_active: program.is_active
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    
    let featuresJson;
    try {
      featuresJson = form.features ? JSON.parse(form.features) : [];
    } catch {
      alert('Features must be valid JSON array');
      setSaving(false);
      return;
    }

    const offersArray = Array.isArray(form.program_offers) ? form.program_offers : [];
    const cleanedOffers = offersArray.map((offer, idx) => ({
      id: offer.id || slugify(offer.label || `offer_${idx + 1}`),
      label: offer.label || `Option ${idx + 1}`,
      price_inr: offer.price_inr === '' ? '' : Number(offer.price_inr) || 0,
      weeks: offer.weeks === '' ? '' : Number(offer.weeks) || '',
      access_days: offer.access_days === '' ? '' : (offer.access_days ? Number(offer.access_days) : (offer.weeks ? Number(offer.weeks) * 7 : '')),
      whatsapp_link: offer.whatsapp_link || '',
      description: offer.description || '',
      cta_text: offer.cta_text || ''
    }));

    // Fix image path - remove API_URL if present (backend will prepend it)
    let imagePath = form.image || '';
    let downloadImagePath = form.download_image || '';
    if (imagePath.startsWith(API_URL)) {
      imagePath = imagePath.substring(API_URL.length);
    }
    if (downloadImagePath.startsWith(API_URL)) {
      downloadImagePath = downloadImagePath.substring(API_URL.length);
    }

    // Build payload with all required fields
    const payload = {
      program_id: form.program_id,
      name: form.name,
      tagline: form.tagline || '',
      description: form.description || '',
      duration: form.duration || '',
      time_commitment: form.time_commitment || '',
      features: featuresJson,
      price: form.price || '',
      price_inr: form.price_inr || null,
      image: imagePath,
      download_image: downloadImagePath,
      intro_video_url: form.intro_video_url || '',
      intro_storage_file_uuid: form.intro_storage_file_uuid || null,
      intro_video_height: form.intro_video_height || 150,
      intro_video_width: form.intro_video_width || 180,
      program_type: form.program_type || 'type1',
      whatsapp_link: form.whatsapp_link || '',
      color: form.color || '',
      badge: form.badge || '',
      program_offers: cleanedOffers,
      display_order: 0,
      is_active: form.is_active !== undefined ? form.is_active : 1
    };

    if ((form.program_type || 'type1') === 'type3' && cleanedOffers.length > 0 && (payload.price_inr === null || payload.price_inr === '')) {
      payload.price_inr = cleanedOffers[0].price_inr || 0;
    }

    console.log('Saving program with payload:', payload);

    try {
      const url = editingId 
        ? `${API_URL}/api/programs/${editingId}`
        : `${API_URL}/api/programs`;
      
      const method = editingId ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(payload)
      });

      const responseData = await res.json();
      console.log('Server response:', res.status, responseData);

      if (res.ok) {
        fetchPrograms();
        setDialogOpen(false);
      } else {
        alert(responseData.error || responseData.message || 'Failed to save program');
      }
    } catch (error) {
      console.error('Error saving program:', error);
      alert('Failed to save program: ' + error.message);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this program? This cannot be undone.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/programs/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (res.ok) {
        fetchPrograms();
      }
    } catch (error) {
      console.error('Error deleting program:', error);
    }
  };

  const toggleStatus = async (program) => {
    const newStatus = program.is_active === 1 ? 0 : 1;
    try {
      const res = await fetch(`${API_URL}/api/programs/${program.program_id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({ is_active: newStatus })
      });
      
      if (res.ok) {
        fetchPrograms();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  // Handle image upload
  const handleImageUpload = async (file) => {
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Only image files (JPG, PNG, GIF, WebP) are allowed');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB');
      return;
    }
    
    setUploading(true);
    setUploadError(null);
    
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const res = await fetch(`${API_URL}/api/upload/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });
      
      const data = await res.json();
      
      if (data.success) {
        setForm(f => ({ ...f, image: data.url }));
      } else {
        setUploadError(data.error || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadError('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };
  
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  
  const clearImage = () => {
    setForm(f => ({ ...f, image: '' }));
  };

  const openGallery = (type = 'main') => {
    setImageSelectionType(type);
    setGalleryOpen(true);
  };

  const handleSelectImage = (url) => {
    if (imageSelectionType === 'download') {
      setForm(f => ({ ...f, download_image: url }));
    } else {
      setForm(f => ({ ...f, image: url }));
    }
  };

  // Offer helpers (Layout 3)
  const slugify = (text = '') => text.toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const addOffer = () => {
    setForm(f => ({
      ...f,
      program_offers: [...(f.program_offers || []), { id: `offer_${(f.program_offers?.length || 0) + 1}`, label: '', price_inr: '', weeks: '', whatsapp_link: '', description: '' }]
    }));
  };

  const updateOfferField = (index, field, value) => {
    setForm(f => {
      const offers = [...(f.program_offers || [])];
      const next = { ...(offers[index] || {}) };
      next[field] = value;
      if (field === 'label' && !next.id) {
        next.id = slugify(value || `offer_${index + 1}`);
      }
      offers[index] = next;
      return { ...f, program_offers: offers };
    });
  };

  const removeOffer = (index) => {
    setForm(f => {
      const offers = [...(f.program_offers || [])];
      offers.splice(index, 1);
      return { ...f, program_offers: offers };
    });
  };

  // Drag and drop handlers for the form image area
  const [dragActive, setDragActive] = useState(false);

  const handleFormDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleFormDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      await handleImageUpload(file);
    }
  }, []);

  // Helper to get full image URL
  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    // Fix paths that are missing /uploads prefix
    if (path.startsWith('/programs/')) {
      path = `/uploads${path}`;
    }
    return `${API_URL}${path}`;
  };

  return (
    <div className="admin-programs">
      <div className="programs-header">
        <div>
          <h1>Program Manager</h1>
          <p>Create, edit and manage your fitness programs.</p>
        </div>
        <button className="btn-primary" onClick={openCreate}>
          <Plus className="h-4 w-4" /> New Program
        </button>
      </div>

      <div className="programs-table-card">
        <table className="programs-table">
          <thead>
            <tr>
              <th>Program</th>
              <th>Duration</th>
              <th>Price</th>
              <th>Layout</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center py-10">Loading...</td>
              </tr>
            ) : programs.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-10">No programs yet. Create one!</td>
              </tr>
            ) : (
              programs.map(program => (
                <tr key={program.program_id}>
                  <td>
                    <div className="program-info">
                      <p className="program-name">{program.name}</p>
                      <p className="program-tagline">{program.tagline}</p>
                    </div>
                  </td>
                  <td>{program.duration}</td>
                  <td className="font-medium">Rs. {program.priceINR || program.price_inr || '0'}</td>
                  <td>{(() => { const t = (program.program_type || 'type1').toLowerCase(); return t.startsWith('type') ? `Layout ${t.slice(4)}` : 'Layout 1'; })()}</td>
                  <td>
                    <span className={`badge ${program.is_active === 1 ? 'badge-active' : 'badge-inactive'}`}>
                      {program.is_active === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="text-right">
                    <div className="action-buttons">
                      <button 
                        className="btn-icon" 
                        onClick={() => toggleStatus(program)} 
                        title="Toggle status"
                      >
                        {program.is_active === 1 ? 
                          <ToggleRight className="h-4 w-4 text-green-500" /> : 
                          <ToggleLeft className="h-4 w-4 text-gray-400" />
                        }
                      </button>
                      <button 
                        className="btn-icon" 
                        onClick={() => openEdit(program)}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button 
                        className="btn-icon text-red-500" 
                        onClick={() => handleDelete(program.program_id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      {dialogOpen && (
        <div className="dialog-overlay" onClick={() => setDialogOpen(false)}>
          <div className="dialog-content" onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h2>{editingId ? 'Edit Program' : 'Create New Program'}</h2>
            </div>
            <div className="dialog-body">
              <div className="form-group">
                <label>Program ID *</label>
                <input 
                  type="text" 
                  value={form.program_id}
                  onChange={e => setForm(f => ({ ...f, program_id: e.target.value }))}
                  placeholder="e.g., P1, P2"
                  disabled={editingId}
                />
              </div>
              <div className="form-group">
                <label>Name *</label>
                <input 
                  type="text" 
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Program name"
                />
              </div>
              <div className="form-group">
                <label>Tagline</label>
                <input 
                  type="text" 
                  value={form.tagline}
                  onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))}
                  placeholder="Short tagline"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea 
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows="3"
                  placeholder="Program description"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Duration</label>
                  <input 
                    type="text" 
                    value={form.duration}
                    onChange={e => setForm(f => ({ ...f, duration: e.target.value }))}
                    placeholder="e.g., 14 Days"
                  />
                </div>
              <div className="form-group">
                <label>Time Commitment</label>
                <input 
                  type="text" 
                  value={form.time_commitment}
                  onChange={e => setForm(f => ({ ...f, time_commitment: e.target.value }))}
                  placeholder="e.g., 5-20 mins/day"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Intro Video URL (Learn More page)</label>
                <input
                  type="text"
                  value={form.intro_video_url}
                  onChange={e => setForm(f => ({ ...f, intro_video_url: e.target.value }))}
                  placeholder="https://youtu.be/..."
                />
              </div>
              <div className="form-group">
                <label>Intro Video Height (px)</label>
                <input
                  type="number"
                  value={form.intro_video_height}
                  onChange={e => setForm(f => ({ ...f, intro_video_height: e.target.value }))}
                  placeholder="150"
                  min="120"
                  max="400"
                />
              </div>
              <div className="form-group">
                <label>Intro Video Width (px, max 180)</label>
                <input
                  type="number"
                  value={form.intro_video_width}
                  onChange={e => setForm(f => ({ ...f, intro_video_width: e.target.value }))}
                  placeholder="180"
                  min="80"
                  max="180"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>Intro Backblaze video</label>
                <StorageVideoUpload
                  value={form.intro_storage_file_uuid || ''}
                  onChange={(uuid) => setForm((f) => ({ ...f, intro_storage_file_uuid: uuid }))}
                  disabled={saving}
                  isPrivate
                  label="Upload"
                  pathSegments={buildProgramPathSegments({
                    programName: form.name,
                    programId: form.program_id || editingId,
                    section: 'Intro',
                  })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Price (INR) *</label>
                  <input 
                    type="number" 
                    value={form.price_inr}
                    onChange={e => setForm(f => ({ ...f, price_inr: e.target.value }))}
                    placeholder="e.g., 2400"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Layout Type</label>
                <select
                  value={form.program_type || 'type1'}
                  onChange={(e) => {
                    const newType = e.target.value;
                    const prev = form.program_type || 'type1';
                    if (newType === prev) {
                      setForm(f => ({ ...f, program_type: newType }));
                      return;
                    }
                    setPendingLayoutType(newType);
                    setLayoutConfirmText('');
                    setLayoutConfirmOpen(true);
                  }}
                >
                  <option value="type1">Layout 1</option>
                  <option value="type2">Layout 2</option>
                  <option value="type3">Layout 3</option>
                  <option value="type4">Layout 4</option>
                  <option value="type5">Layout 5</option>
                  <option value="type6">Layout 6</option>
                  <option value="type7">Layout 7</option>
                </select>
                <small style={{ color: '#b45309' }}>Warning: switching layouts may require re-entering content.</small>
              </div>
              <div className="form-group">
                <label>Community / Group Link (for layout 2 button)</label>
                <input
                  type="url"
                  value={form.whatsapp_link || ''}
                  onChange={e => setForm(f => ({ ...f, whatsapp_link: e.target.value }))}
                  placeholder="https://wa.me/1234567890 or community link"
                />
              </div>

              {(form.program_type || 'type1') === 'type3' && (
                <div className="offer-builder">
                  <div className="offer-builder-header">
                    <div>
                      <h4>Layout 3 Pricing Options</h4>
                      <p>Add multiple pricing/week combos. Buttons will show stacked on the Learn More page.</p>
                    </div>
                    <button type="button" className="btn-secondary" onClick={addOffer}>
                      <Plus size={16} /> Add Option
                    </button>
                  </div>
                  {(form.program_offers || []).length === 0 && (
                    <div className="offer-empty">No offers yet. Add at least one option.</div>
                  )}
                  <div className="offer-list">
                    {(form.program_offers || []).map((offer, idx) => (
                      <div key={offer.id || idx} className="offer-row">
                        <div className="offer-row-left">
                          <label>Label</label>
                          <input
                            type="text"
                            value={offer.label || ''}
                            onChange={e => updateOfferField(idx, 'label', e.target.value)}
                            placeholder="e.g., 4 Weeks"
                          />
                          <small>ID: {offer.id || slugify(offer.label || `offer_${idx + 1}`)}</small>
                        </div>
                        <div className="offer-row-mid">
                          <label>Price (INR)</label>
                          <input
                            type="number"
                            value={offer.price_inr}
                            onChange={e => updateOfferField(idx, 'price_inr', e.target.value)}
                            placeholder="2400"
                          />
                        </div>
                        <div className="offer-row-mid">
                          <label>Weeks</label>
                          <input
                            type="number"
                            value={offer.weeks}
                            onChange={e => updateOfferField(idx, 'weeks', e.target.value)}
                            placeholder="4"
                          />
                        </div>
                        <div className="offer-row-right">
                          <label>WhatsApp Link</label>
                          <input
                            type="url"
                            value={offer.whatsapp_link || ''}
                            onChange={e => updateOfferField(idx, 'whatsapp_link', e.target.value)}
                            placeholder="https://chat.whatsapp.com/..."
                          />
                        </div>
                        <div className="offer-row-wide">
                          <label>Description (optional)</label>
                          <input
                            type="text"
                            value={offer.description || ''}
                            onChange={e => updateOfferField(idx, 'description', e.target.value)}
                            placeholder="Short note shown under the label"
                          />
                        </div>
                        <div className="offer-row-wide">
                          <label>Button Text (optional)</label>
                          <input
                            type="text"
                            value={offer.cta_text || ''}
                            onChange={e => updateOfferField(idx, 'cta_text', e.target.value)}
                            placeholder="Buy Now / Join Now"
                          />
                        </div>
                        <button type="button" className="btn-icon text-red-500" onClick={() => removeOffer(idx)} title="Remove offer">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Badge</label>
                  <input 
                    type="text" 
                    value={form.badge}
                    onChange={e => setForm(f => ({ ...f, badge: e.target.value }))}
                    placeholder="e.g., Most Popular"
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <div 
                    onClick={() => setForm(f => ({ ...f, is_active: f.is_active === 1 ? 0 : 1 }))}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      padding: '8px 0'
                    }}
                  >
                    {form.is_active === 1 ? (
                      <>
                        <ToggleRight className="h-8 w-8 text-green-500" />
                        <span style={{ color: '#166534', fontWeight: 500 }}>Active</span>
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="h-8 w-8 text-gray-400" />
                        <span style={{ color: '#6b7280', fontWeight: 500 }}>Inactive</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Image Upload */}
              <div className="form-group">
                <label>Program Image</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  style={{ display: 'none' }}
                />
                
                {form.image ? (
                  <div 
                    className="image-preview-compact" 
                    onClick={() => openGallery('main')}
                  >
                    <img 
                      src={getImageUrl(form.image)}
                      alt=""
                      crossOrigin="anonymous"
                    />
                    <button 
                      type="button" 
                      className="btn-remove-image"
                      onClick={(e) => { e.stopPropagation(); clearImage(); }}
                    >
                      <X size={14} />
                    </button>
                    <div className="image-change-hint">
                      <span>Click to change</span>
                    </div>
                  </div>
                ) : (
                  <div 
                    className={`upload-compact ${uploading ? 'uploading' : ''} ${dragActive ? 'drag-active' : ''}`}
                    onClick={() => openGallery('main')}
                    onDragEnter={handleFormDrag}
                    onDragLeave={handleFormDrag}
                    onDragOver={handleFormDrag}
                    onDrop={handleFormDrop}
                  >
                    {uploading ? (
                      <div className="upload-spinner-small" />
                    ) : (
                      <>
                        <ImageIcon size={20} />
                        <span>{dragActive ? 'Drop here' : 'Click or drop image'}</span>
                      </>
                    )}
                  </div>
                )}
                
                {uploadError && <p className="upload-error">{uploadError}</p>}
              </div>

              {/* Download Image Upload */}
              <div className="form-group">
                <label>Download Image (for purchased users)</label>
                <small style={{ display: 'block', marginBottom: '8px', color: '#666' }}>
                  This image will be shown below the main image and can be downloaded by users who purchased the program.
                </small>
                
                {form.download_image ? (
                  <div 
                    className="image-preview-compact" 
                    onClick={() => openGallery('download')}
                  >
                    <img 
                      src={getImageUrl(form.download_image)}
                      alt=""
                      crossOrigin="anonymous"
                    />
                    <button 
                      type="button" 
                      className="btn-remove-image"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        setForm(f => ({ ...f, download_image: '' }));
                      }}
                    >
                      <X size={14} />
                    </button>
                    <div className="image-change-hint">
                      <span>Click to change download image</span>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="upload-compact"
                    onClick={() => openGallery('download')}
                  >
                    <ImageIcon size={20} />
                    <span>Click to select download image</span>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Color (CSS Gradient)</label>
                <input 
                  type="text" 
                  value={form.color}
                  onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                  placeholder="linear-gradient(...)"
                />
              </div>
              <div className="form-group">
                <label>Features (JSON Array)</label>
                <textarea 
                  value={form.features}
                  onChange={e => setForm(f => ({ ...f, features: e.target.value }))}
                  rows="3"
                  className="font-mono text-xs"
                  placeholder='["Feature 1", "Feature 2"]'
                />
              </div>
            </div>
            <ImageGallery 
              isOpen={galleryOpen} 
              onClose={() => setGalleryOpen(false)}
              onSelect={handleSelectImage}
              currentImage={imageSelectionType === 'download' ? form.download_image : form.image}
            />
            <div className="dialog-footer">
              <button className="btn-secondary" onClick={() => setDialogOpen(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSave} 
                disabled={saving || !form.program_id || !form.name}
              >
                {saving ? 'Saving?' : 'Save Program'}
              </button>
            </div>
          </div>
        </div>
      )}

      {layoutConfirmOpen && (
        <div className="dialog-overlay" onClick={() => setLayoutConfirmOpen(false)}>
          <div className="dialog-content" onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Confirm layout change</h3>
            </div>
            <div className="dialog-body">
              <p style={{ marginBottom: 12 }}>
                Changing layout will change which fields and UI are used for this program. Only do this if you are ready to reconfigure the program layout.
              </p>
              <p style={{ marginBottom: 12 }}>
                Type <strong>CONFIRM</strong> to switch to Layout {pendingLayoutType ? pendingLayoutType.replace('type','') : ''}.
              </p>
              <input
                type="text"
                value={layoutConfirmText}
                onChange={e => setLayoutConfirmText(e.target.value)}
                placeholder="Type CONFIRM to continue"
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #e5e7eb' }}
              />
            </div>
            <div className="dialog-footer">
              <button className="btn-secondary" onClick={() => { setLayoutConfirmOpen(false); setPendingLayoutType(null); }}>
                Cancel
              </button>
              <button
                className="btn-primary"
                disabled={layoutConfirmText !== 'CONFIRM' || !pendingLayoutType}
                onClick={() => {
                  if (layoutConfirmText !== 'CONFIRM' || !pendingLayoutType) return;
                  setForm(f => ({ ...f, program_type: pendingLayoutType }));
                  setLayoutConfirmOpen(false);
                  setPendingLayoutType(null);
                  setLayoutConfirmText('');
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}






